"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CurrencyConfig } from '../utils/currency';
import { useCurrencySettings } from '../utils/currencySettings';
import axiosInstance from '../config/axios';

// Configuração padrão local para evitar importação circular
const DEFAULT_CURRENCY_CONFIG: CurrencyConfig = {
  code: 'USD',
  symbol: '$',
  position: 'before'
};

interface CurrencyContextType {
  currencyConfig: CurrencyConfig;
  loading: boolean;
  error: string | null;
  updateCurrencyFromApiResponse: (apiResponse: any) => void;
}

const CurrencyContext = createContext<CurrencyContextType>({
  currencyConfig: DEFAULT_CURRENCY_CONFIG,
  loading: true,
  error: null,
  updateCurrencyFromApiResponse: () => {}
});

export function useCurrencyContext() {
  return useContext(CurrencyContext);
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currencyConfig, setCurrencyConfig] = useState<CurrencyConfig>(DEFAULT_CURRENCY_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Buscar a configuração da loja usando axios ao invés de fetch
    // Esta função ainda é necessária para inicializar o contexto,
    // mas os componentes individuais também podem atualizar o contexto quando recebem dados da API
    axiosInstance.get('/store-config')
      .then(response => {
        const data = response.data;
        if (data && data.currency) {
          setCurrencyConfig(data.currency);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Erro ao buscar configuração de moeda:', err);
        setError('Erro ao carregar configuração de moeda');
        setLoading(false);
      });
  }, []);
  
  // Expondo método para atualizar a configuração de moeda a partir dos dados da API
  const updateCurrencyFromApiResponse = (apiResponse: any) => {
    if (apiResponse && apiResponse.currency) {
      setCurrencyConfig(apiResponse.currency);
      if (loading) setLoading(false);
    }
  };
  
  // Adicionado à interface do contexto
  const contextValue = { 
    currencyConfig, 
    loading, 
    error,
    updateCurrencyFromApiResponse
  };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
}
